// ============================================
// NOTIFICATION BELL — F22A + HOTFIX3
// ============================================
// ✅ HOTFIX3: Portal en document.body (no se corta por sidebar overflow)
// ✅ HOTFIX3: Posición calculada desde el botón (fixed, no absolute)
// ✅ HOTFIX3: Polling cada 15s como fallback de socket
// ✅ HOTFIX3: Click en notificación → navega al /chat
// Campana de notificaciones con:
// - Badge con contador de no leídas
// - Panel desplegable con lista scrollable
// - Socket listener para tiempo real
// - Browser Notification API (push nativo)
// - Auto-close al hacer click fuera
// ============================================

import { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useSocket } from '../../context/SocketContext';
import { useToast } from '../../context/ToastContext';
import notificationService from '../../services/notificationServiceFE';

// ============================================
// HELPER: Tiempo relativo
// ============================================
const timeAgo = (dateStr) => {
  const now = new Date();
  const date = new Date(dateStr);
  const diffMs = now - date;
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHr = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHr / 24);

  if (diffSec < 60) return 'ahora';
  if (diffMin < 60) return `${diffMin}m`;
  if (diffHr < 24) return `${diffHr}h`;
  if (diffDay < 7) return `${diffDay}d`;
  return date.toLocaleDateString('es-AR', { day: 'numeric', month: 'short' });
};

// ============================================
// BROWSER NOTIFICATIONS
// ============================================
const requestNotificationPermission = async () => {
  if (!('Notification' in window)) return 'denied';
  if (Notification.permission === 'granted') return 'granted';
  if (Notification.permission !== 'denied') {
    return await Notification.requestPermission();
  }
  return Notification.permission;
};

const showBrowserNotification = (title, body, icon = '🔔') => {
  if (Notification.permission !== 'granted') return;
  try {
    new Notification(title, {
      body,
      icon: '/ojo.png',
      badge: '/ojo.png',
      tag: 'bileads-notification',
      renotify: true
    });
  } catch (e) {
    // Safari/mobile no soportan todas las opciones
    console.warn('Browser notification fallback:', e.message);
  }
};

// ============================================
// COMPONENTE PRINCIPAL
// ============================================
const NotificationBell = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [notificaciones, setNotificaciones] = useState([]);
  const [noLeidas, setNoLeidas] = useState(0);
  const [loading, setLoading] = useState(false);
  // ✅ S44 R1-B: Tab principal (RECORDATORIOS o SISTEMA)
  const [mainTab, setMainTab] = useState('RECORDATORIOS');
  // ✅ BLOQUE NOTIF: Sub-tabs del tab Sistema
  const [activeTab, setActiveTab] = useState('URGENTES');
  const [counts, setCounts] = useState({ total: 0, urgentes: 0, servicios: 0, informativas: 0 });
  // ✅ S44 R1-B: Datos de mis-tareas
  const [misTareas, setMisTareas] = useState({ vencidos: [], hoy: [], proximos: [], conteo: { vencidos: 0, hoy: 0, proximos: 0, total: 0 } });
  const [tareasLoading, setTareasLoading] = useState(false);
  const panelRef = useRef(null);
  const bellRef = useRef(null);
  const toast = useToast();
  const navigate = useNavigate();

  // ✅ HOTFIX3: Posición del panel calculada desde el botón
  const [panelPos, setPanelPos] = useState({ top: 0, left: 0 });

  // Socket
  const socketContext = useSocket();
  const socket = socketContext?.socket || socketContext;

  // ============================================
  // CARGAR NOTIFICACIONES
  // ============================================
const cargarNotificaciones = useCallback(async (categoria = activeTab) => {
    try {
      setLoading(true);
      const [notifData, countsData] = await Promise.all([
        notificationService.getNotificaciones({ limit: 30, categoria }),
        notificationService.getCountCategorias()
      ]);
      setNotificaciones(notifData.data || []);
      setNoLeidas(countsData.total || 0);
      setCounts({
        total: countsData.total || 0,
        urgentes: countsData.urgentes || 0,
        servicios: countsData.servicios || 0,
        informativas: countsData.informativas || 0
      });
    } catch (error) {
      console.error('Error cargando notificaciones:', error);
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  // Cargar al montar
  useEffect(() => {
    cargarNotificaciones();
    requestNotificationPermission();
  }, [cargarNotificaciones]);

  // ✅ HOTFIX3: Polling cada 15s — notificaciones
  useEffect(() => {
    const interval = setInterval(async () => {
      try {
        const count = await notificationService.getNotificacionesCount();
        setNoLeidas(prev => {
          if (count > prev && count > 0) {
            if (isOpen) cargarNotificaciones();
          }
          return count;
        });
      } catch { /* silencioso */ }
    }, 15000);
    return () => clearInterval(interval);
  }, [isOpen, cargarNotificaciones]);

// ✅ BLOQUE NOTIF: Recargar cuando cambia de tab
  useEffect(() => {
    if (isOpen) cargarNotificaciones(activeTab);
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  // ============================================
  // SOCKET: Escuchar nueva_notificacion
  // ============================================
  useEffect(() => {
    if (!socket || typeof socket.on !== 'function') return;

    const handleNuevaNotificacion = (data) => {
      const { notificacion, noLeidas: count } = data;
      
      // Agregar al inicio de la lista
      setNotificaciones(prev => [notificacion, ...prev].slice(0, 50));
      setNoLeidas(count);

      // Browser push notification (si el tab no está visible)
      if (document.visibilityState !== 'visible') {
        showBrowserNotification(
          notificacion.titulo,
          notificacion.mensaje
        );
      }

      // Toast en-app (siempre)
      const icon = notificationService.getNotifIcon(notificacion.tipo);
      if (notificacion.tipo === 'SPEED_TO_LEAD') {
        toast.error(`${icon} ${notificacion.titulo}`);
      } else {
        toast.info(`${icon} ${notificacion.titulo}`);
      }
    };

    socket.on('nueva_notificacion', handleNuevaNotificacion);

    return () => {
      if (socket && typeof socket.off === 'function') {
        socket.off('nueva_notificacion', handleNuevaNotificacion);
      }
    };
  }, [socket, toast]);

  // ============================================
  // CLICK FUERA → Cerrar panel
  // ============================================
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        panelRef.current && !panelRef.current.contains(event.target) &&
        bellRef.current && !bellRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // ============================================
  // ACCIONES
  // ============================================
  const handleToggle = () => {
    if (!isOpen && bellRef.current) {
      // ✅ HOTFIX3: Calcular posición del panel desde el botón
      const rect = bellRef.current.getBoundingClientRect();
      setPanelPos({
        top: rect.bottom + 8,
        left: Math.max(8, rect.left) // Nunca fuera de pantalla izquierda
      });
    }
   setIsOpen(prev => !prev);
    if (!isOpen) {
      cargarNotificaciones();
      cargarMisTareas();
    }
  };

  const handleMarcarLeida = async (notif) => {
    if (!notif.leido) {
      try {
        await notificationService.marcarLeida(notif.id);
        setNotificaciones(prev =>
          prev.map(n => n.id === notif.id ? { ...n, leido: true } : n)
        );
        setNoLeidas(prev => Math.max(0, prev - 1));
      } catch (error) {
        console.error('Error marcando leída:', error);
      }
    }
    // ✅ Navegar según tipo de notificación
    if (notif.tipo === 'RECORDATORIO') {
      setIsOpen(false);
      navigate('/pipeline');
    } else if (notif.tipo === 'SERVICIO_PENDIENTE_AGRUPADO' || notif.tipo === 'SERVICIO_PENDIENTE') {
      setIsOpen(false);
      navigate('/service-alerts');
    } else if (notif.tipo === 'LEAD_RECONTACTO_OCULTO' && notif.metadata?.conversacionId) {
      // ✅ S51: Navegar directo a la conversación reactivada
      setIsOpen(false);
      navigate(`/chat?conversacion=${notif.metadata.conversacionId}`);
    } else if (notif.metadata?.conversacionId || notif.metadata?.leadId) {
      setIsOpen(false);
      navigate('/chat');
    }
  };

  const handleMarcarTodas = async () => {
    try {
      await notificationService.marcarTodasLeidas();
      setNotificaciones(prev => prev.map(n => ({ ...n, leido: true })));
      setNoLeidas(0);
    } catch (error) {
      console.error('Error marcando todas:', error);
    }
  };

  // ============================================
  // S44 R1-B: FUNCIONES RECORDATORIOS
  // ============================================
  const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';
  const authHeaders = () => ({
    'Authorization': `Bearer ${localStorage.getItem('token')}`,
    'Content-Type': 'application/json'
  });

  const cargarMisTareas = useCallback(async () => {
    try {
      setTareasLoading(true);
      const res = await fetch(`${API_URL}/recordatorios/mis-tareas`, {
        headers: authHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        if (data.success) setMisTareas(data);
      }
    } catch (error) {
      console.error('Error cargando mis tareas:', error);
    } finally {
      setTareasLoading(false);
    }
  }, []);

  const handleCompletarRec = async (recId) => {
    try {
      const res = await fetch(`${API_URL}/recordatorios/${recId}/completar`, {
        method: 'PUT', headers: authHeaders()
      });
      if (res.ok) {
        toast.success('✅ Completado');
        cargarMisTareas();
      }
    } catch { toast.error('Error al completar'); }
  };

  // ✅ S55: Reprogramar con fecha seleccionable (antes: hardcoded +1 día)
  const [reprogramarId, setReprogramarId] = useState(null);

  const handleReprogramarRec = async (recId, datetimeStr) => {
    try {
      // ✅ S57: Extraer fecha y hora del datetime-local (formato "2026-07-11T15:00")
      const [fechaPart, horaPart] = datetimeStr.split('T');
      const res = await fetch(`${API_URL}/recordatorios/${recId}`, {
        method: 'PUT', headers: authHeaders(),
        body: JSON.stringify({ fechaRecordatorio: fechaPart, hora: horaPart || '09:00', notificado: false })
      });
      if (res.ok) {
        const fechaDisplay = new Date(datetimeStr).toLocaleString('es-AR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
        toast.info(`📅 Reprogramado para ${fechaDisplay}`);
        setReprogramarId(null);
        await cargarMisTareas();
      }
    } catch { toast.error('Error al reprogramar'); }
  };

  const handlePosponerTodos = async () => {
    try {
      const res = await fetch(`${API_URL}/recordatorios/posponer-vencidos`, {
        method: 'PATCH', headers: authHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        toast.info(`⏭️ ${data.count} pospuesto(s) a mañana`);
        cargarMisTareas();
      }
    } catch { toast.error('Error al posponer'); }
  };

  // Cargar mis-tareas al montar + polling cada 15s
  useEffect(() => {
    cargarMisTareas();
    const interval = setInterval(() => cargarMisTareas(), 15000);
    return () => clearInterval(interval);
  }, [cargarMisTareas]);

  // ============================================
  // RENDER
  // ============================================
  return (
    <div style={{ position: 'relative' }}>
      {/* Botón campana */}
      <button
        ref={bellRef}
        onClick={handleToggle}
        title="Notificaciones"
        style={{
          background: isOpen ? 'rgba(0, 246, 237, 0.15)' : 'transparent',
          border: 'none',
          borderRadius: 8,
          padding: '6px 8px',
          cursor: 'pointer',
          position: 'relative',
          transition: 'background 0.2s',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
        onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.08)'}
        onMouseLeave={e => {
          if (!isOpen) e.currentTarget.style.background = 'transparent';
        }}
      >
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#e0e0e0" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>

        {/* Badge — ✅ S44: combina notificaciones + recordatorios pendientes */}
        {(() => {
          const recPendientes = (misTareas.conteo?.vencidos || 0) + (misTareas.conteo?.hoy || 0);
          const totalBadge = noLeidas + recPendientes;
          const badgeColor = (misTareas.conteo?.vencidos || 0) > 0 ? '#c12139'
            : recPendientes > 0 ? '#f59e0b'
            : '#c12139';
          if (totalBadge <= 0) return null;
          return (
            <span style={{
              position: 'absolute',
              top: 2,
              right: 2,
              background: badgeColor,
              color: '#fff',
              fontSize: 10,
              fontWeight: 700,
              borderRadius: '50%',
              minWidth: 16,
              height: 16,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '0 4px',
              lineHeight: 1,
              boxShadow: '0 0 0 2px #0a0f1a'
            }}>
              {totalBadge > 99 ? '99+' : totalBadge}
            </span>
          );
        })()}
      </button>

      {/* Panel desplegable — ✅ HOTFIX3: Portal para que no lo corte el sidebar */}
      {isOpen && createPortal(
        <>
          {/* Backdrop invisible que bloquea clics y fuerza z-index */}
          <div
            onClick={() => setIsOpen(false)}
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 999998,
              background: 'rgba(0,0,0,0.3)'
            }}
          />
          <div
            ref={panelRef}
            style={{
              position: 'fixed',
              top: panelPos.top,
              left: panelPos.left,
              width: 370,
              maxHeight: 480,
              background: '#141a26',
              border: '1px solid rgba(255,255,255,0.12)',
              borderRadius: 12,
              boxShadow: '0 20px 60px rgba(0,0,0,0.6), 0 0 0 1px rgba(0,246,237,0.08)',
              zIndex: 999999,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden'
            }}
          >
          {/* ✅ S44: Header */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '14px 16px', borderBottom: '1px solid rgba(255,255,255,0.08)', background: '#0e1420'
          }}>
            <span style={{ color: '#fff', fontWeight: 600, fontSize: 15 }}>Notificaciones</span>
            {mainTab === 'SISTEMA' && noLeidas > 0 && (
              <button onClick={handleMarcarTodas} style={{
                background: 'none', border: 'none', color: '#00F6ED', fontSize: 12,
                cursor: 'pointer', fontWeight: 500, padding: '2px 6px', borderRadius: 4
              }}>Marcar todas leídas</button>
            )}
          </div>

          {/* ✅ S44 R1-B: Tabs principales — Recordatorios / Sistema */}
          <div style={{
            display: 'flex', borderBottom: '1px solid rgba(255,255,255,0.08)', background: '#0e1420'
          }}>
            {[
              { key: 'RECORDATORIOS', label: '📅 Recordatorios', count: misTareas.conteo?.total || 0, color: '#f59e0b' },
              { key: 'SISTEMA', label: '🔔 Sistema', count: noLeidas, color: '#c12139' }
            ].map(tab => (
              <button key={tab.key} onClick={() => setMainTab(tab.key)} style={{
                flex: 1, padding: '10px 8px', background: 'transparent', border: 'none',
                borderBottom: mainTab === tab.key ? `2px solid ${tab.color}` : '2px solid transparent',
                color: mainTab === tab.key ? '#fff' : '#9ca3af', fontSize: 12,
                fontWeight: mainTab === tab.key ? 600 : 400, cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4
              }}>
                <span>{tab.label}</span>
                {tab.count > 0 && (
                  <span style={{
                    background: tab.color, color: '#fff', fontSize: 10, fontWeight: 700,
                    borderRadius: 10, padding: '1px 6px', minWidth: 18, textAlign: 'center'
                  }}>{tab.count > 99 ? '99+' : tab.count}</span>
                )}
              </button>
            ))}
          </div>

          {/* ✅ S44 R1-B: Contenido tab RECORDATORIOS */}
          {mainTab === 'RECORDATORIOS' && (
            <div style={{ overflowY: 'auto', flex: 1, maxHeight: 380 }}>
              {tareasLoading && misTareas.conteo?.total === 0 ? (
                <div style={{ padding: 30, textAlign: 'center', color: '#6b7280' }}>Cargando...</div>
              ) : misTareas.conteo?.total === 0 ? (
                <div style={{ padding: 40, textAlign: 'center' }}>
                  <div style={{ fontSize: 32, marginBottom: 8 }}>✅</div>
                  <div style={{ color: '#6b7280', fontSize: 13 }}>Sin recordatorios pendientes</div>
                </div>
              ) : (
                <>
                  {/* Renderizar sección */}
                  {[
                    { key: 'vencidos', label: '🔴 Vencidos', items: misTareas.vencidos, color: '#ef4444', showPosponerTodos: true },
                    { key: 'hoy', label: '🟡 Hoy', items: misTareas.hoy, color: '#f59e0b' },
                    { key: 'proximos', label: '🟢 Próximos 7 días', items: misTareas.proximos, color: '#10b981' }
                  ].map(section => section.items?.length > 0 && (
                    <div key={section.key}>
                      <div style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        padding: '8px 16px', background: `${section.color}10`,
                        borderBottom: '1px solid rgba(255,255,255,0.04)'
                      }}>
                        <span style={{ fontSize: 12, fontWeight: 700, color: section.color }}>
                          {section.label} ({section.items.length})
                        </span>
                        {section.showPosponerTodos && section.items.length > 1 && (
                          <button onClick={handlePosponerTodos} style={{
                            background: 'none', border: '1px solid #ef444440', borderRadius: 4,
                            color: '#ef4444', fontSize: 10, cursor: 'pointer', padding: '2px 8px'
                          }}>⏭️ Posponer todos</button>
                        )}
                      </div>
                      {section.items.map(rec => (
                        <div key={rec.id} style={{
                          display: 'flex', gap: 8, padding: '10px 16px',
                          borderBottom: '1px solid rgba(255,255,255,0.04)',
                          alignItems: 'flex-start'
                        }}>
                          <div style={{
                            width: 32, height: 32, borderRadius: '50%',
                            background: `${section.color}20`, display: 'flex',
                            alignItems: 'center', justifyContent: 'center', fontSize: 14, flexShrink: 0
                          }}>📅</div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{
                              color: '#fff', fontSize: 13, fontWeight: 600, lineHeight: 1.3,
                              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                            }}>
                              {rec.titulo || rec.descripcion?.substring(0, 50) || 'Sin título'}
                            </div>
                            <div style={{ color: '#6b7280', fontSize: 12, marginTop: 2 }}>
                              {rec.lead?.nombre}
                              {rec.fechaRecordatorio && ` · ${new Date(rec.fechaRecordatorio).toLocaleDateString('es-AR', { weekday: 'short', day: 'numeric', month: 'short' })}`}
                              {rec.hora ? ` ${rec.hora}` : ''}
                              {rec.prioridad && rec.prioridad !== 'MEDIA' && (
                                <span style={{
                                  marginLeft: 6, fontSize: 10, padding: '1px 5px', borderRadius: 4,
                                  background: rec.prioridad === 'ALTA' ? '#ef444420' : '#10b98120',
                                  color: rec.prioridad === 'ALTA' ? '#ef4444' : '#10b981'
                                }}>{rec.prioridad}</span>
                              )}
                            </div>
                            {rec.descripcion && rec.titulo && (
                              <div style={{ color: '#4b5563', fontSize: 11, marginTop: 2 }}>
                                {rec.descripcion.length > 60 ? rec.descripcion.substring(0, 60) + '...' : rec.descripcion}
                              </div>
                            )}
                            {/* Botones de acción */}
                            <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                              <button onClick={() => handleCompletarRec(rec.id)} style={{
                                background: '#10b98115', border: '1px solid #10b98130', borderRadius: 4,
                                color: '#10b981', fontSize: 11, cursor: 'pointer', padding: '3px 10px',
                                fontWeight: 600
                              }}>✅ Completar</button>
                              {reprogramarId === rec.id ? (
                                <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                  <input
                                    type="datetime-local"
                                    min={new Date().toISOString().slice(0, 16)}
                                    defaultValue={new Date(Date.now() + 86400000).toISOString().slice(0, 16)}
                                    onChange={(e) => { if (e.target.value) handleReprogramarRec(rec.id, e.target.value); }}
                                    style={{
                                      background: '#0d1117', border: '1px solid #00F6ED50', borderRadius: 4,
                                      color: '#e6edf3', fontSize: 11, padding: '2px 6px', colorScheme: 'dark',
                                      width: 190, cursor: 'pointer'
                                    }}
                                    autoFocus
                                  />
                                  <button onClick={() => setReprogramarId(null)} style={{
                                    background: 'none', border: 'none', color: '#6e7681', fontSize: 13,
                                    cursor: 'pointer', padding: '0 4px'
                                  }}>✕</button>
                                </span>
                              ) : (
                                <button onClick={() => setReprogramarId(rec.id)} style={{
                                  background: 'none', border: '1px solid #30363d', borderRadius: 4,
                                  color: '#8b949e', fontSize: 11, cursor: 'pointer', padding: '3px 10px'
                                }}>📅 Reprogramar</button>
                              )}
                              <button onClick={() => {
                                setIsOpen(false);
                                navigate(`/historial/${rec.leadId}`);
                              }} style={{
                                background: 'none', border: '1px solid #30363d', borderRadius: 4,
                                color: '#8b949e', fontSize: 11, cursor: 'pointer', padding: '3px 10px'
                              }}>👁 Ver</button>
                            </div>
                                                        {rec.lead?.telefono && (<a href={`https://wa.me/${rec.lead.telefono.replace(/\+/g, '')}`} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} style={{ display: 'block', textAlign: 'center', marginTop: 4, background: '#25D36612', border: '1px solid #25D36635', borderRadius: 4, color: '#25D366', fontSize: 11, padding: '5px 0', fontWeight: 600, textDecoration: 'none', cursor: 'pointer' }}>📱 Reabrir conversación en WhatsApp</a>)}
                          </div>
                        </div>
                      ))}
                    </div>
                  ))}
                </>
              )}
            </div>
          )}

          {/* ✅ S44: Contenido tab SISTEMA (notificaciones existentes) */}
          {mainTab === 'SISTEMA' && (
            <>
              {/* Sub-tabs: Urgentes / Servicios / Info */}
              <div style={{
                display: 'flex', borderBottom: '1px solid rgba(255,255,255,0.08)', background: '#0e1420'
              }}>
                {[
                  { key: 'URGENTES', label: '🔔 Urgentes', count: counts.urgentes, color: '#c12139' },
                  { key: 'SERVICIOS', label: '📅 Servicios', count: counts.servicios, color: '#f97316' },
                  { key: 'INFORMATIVAS', label: '✅ Info', count: counts.informativas, color: '#06b6d4' }
                ].map(tab => (
                  <button key={tab.key} onClick={() => setActiveTab(tab.key)} style={{
                    flex: 1, padding: '8px 6px', background: 'transparent', border: 'none',
                    borderBottom: activeTab === tab.key ? `2px solid ${tab.color}` : '2px solid transparent',
                    color: activeTab === tab.key ? '#fff' : '#9ca3af', fontSize: 11,
                    fontWeight: activeTab === tab.key ? 600 : 400, cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 3
                  }}>
                    <span>{tab.label}</span>
                    {tab.count > 0 && (
                      <span style={{
                        background: tab.color, color: '#fff', fontSize: 9, fontWeight: 700,
                        borderRadius: 10, padding: '1px 5px', minWidth: 16, textAlign: 'center'
                      }}>{tab.count > 99 ? '99+' : tab.count}</span>
                    )}
                  </button>
                ))}
              </div>

              {/* Lista de notificaciones del sistema */}
              <div style={{ overflowY: 'auto', flex: 1, maxHeight: 340 }}>
                {loading && notificaciones.length === 0 ? (
                  <div style={{ padding: 30, textAlign: 'center', color: '#6b7280' }}>Cargando...</div>
                ) : notificaciones.length === 0 ? (
                  <div style={{ padding: 40, textAlign: 'center' }}>
                    <div style={{ fontSize: 32, marginBottom: 8 }}>
                      {activeTab === 'URGENTES' ? '✅' : activeTab === 'SERVICIOS' ? '📅' : '📭'}
                    </div>
                    <div style={{ color: '#6b7280', fontSize: 13 }}>
                      {activeTab === 'URGENTES' && 'Sin pendientes urgentes'}
                      {activeTab === 'SERVICIOS' && 'Sin alertas de servicios'}
                      {activeTab === 'INFORMATIVAS' && 'Sin notificaciones informativas'}
                    </div>
                  </div>
                ) : (
                  notificaciones.map((notif) => (
                    <div
                      key={notif.id}
                      onClick={() => handleMarcarLeida(notif)}
                      style={{
                        display: 'flex', gap: 10, padding: '12px 16px',
                        borderBottom: '1px solid rgba(255,255,255,0.04)',
                        cursor: 'pointer',
                        background: notif.leido ? 'transparent' : 'rgba(0, 246, 237, 0.04)',
                        transition: 'background 0.15s'
                      }}
                      onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.05)'}
                      onMouseLeave={e => e.currentTarget.style.background = notif.leido ? 'transparent' : 'rgba(0, 246, 237, 0.04)'}
                    >
                      <div style={{
                        width: 36, height: 36, borderRadius: '50%',
                        background: `${notificationService.getNotifColor(notif.tipo)}20`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 16, flexShrink: 0
                      }}>{notificationService.getNotifIcon(notif.tipo)}</div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{
                          color: notif.leido ? '#9ca3af' : '#fff', fontSize: 13,
                          fontWeight: notif.leido ? 400 : 600, lineHeight: 1.3, marginBottom: 3,
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                        }}>{notif.titulo}</div>
                        {notif.tipo === 'SERVICIO_PENDIENTE_AGRUPADO' && notif.metadata?.totalLeads && (
                          <span style={{
                            display: 'inline-block', background: '#f97316', color: '#fff',
                            fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 10,
                            marginBottom: 4, marginTop: 2
                          }}>{notif.metadata.totalLeads} {notif.metadata.totalLeads === 1 ? 'cliente' : 'clientes'}</span>
                        )}
                        <div style={{
                          color: '#6b7280', fontSize: 12, lineHeight: 1.3,
                          display: '-webkit-box', WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical', overflow: 'hidden'
                        }}>{notif.mensaje}</div>
                        <div style={{ color: '#4b5563', fontSize: 11, marginTop: 4 }}>{timeAgo(notif.createdAt)}</div>
                      </div>
                      {!notif.leido && (
                        <div style={{
                          width: 8, height: 8, borderRadius: '50%', background: '#00F6ED',
                          flexShrink: 0, marginTop: 4
                        }} />
                      )}
                    </div>
                  ))
                )}
              </div>
            </>
          )}
        </div>
        </>,
        document.body
      )}
    </div>
  );
};

export default NotificationBell;
